/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies */
import Model, {
  hasMany,
  belongsTo,
  attr,
  type AsyncBelongsTo,
  type AsyncHasMany,
} from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import { or, alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import type { Type } from '@warp-drive/core/types/symbols';
import type { EnumValue } from '@linc-technologies/linced-common/enums/base-enum';
import PaymentMethod from '@linc-technologies/linced-common/enums/payment-method';
import TransactionType from '@linc-technologies/linced-common/enums/transaction-type';
import { resolved } from '@linc-technologies/linced-common/utils/arrays';
import type Account from '@linc-technologies/linced-common/models/finance/account';
import type LineItem from 'linced-app/models/finance/line-item';
import type TillSession from 'linced-app/models/finance/till-session';
import type Person from 'linced-app/models/person';
import type School from 'linced-app/models/school';
import type FeaturesService from 'linced-app/services/features';
import type FinanceService from 'linced-app/services/finance';

// Proto: finance.Transaction.
export default class Transaction extends Model {
  declare [Type]: 'finance/transaction';

  @service
  declare features: FeaturesService;

  @service
  declare finance: FinanceService;

  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @alias('schoolId')
  declare school: AsyncBelongsTo<School>;

  @attr('string', { defaultValue: '' })
  declare campusId: string; // TODO: implement when we have Campuses

  @attr('number')
  declare updateTime: number | undefined;

  @attr('number')
  declare createTime: number | undefined;

  @belongsTo('finance/account', { async: true, inverse: null })
  declare accountId: AsyncBelongsTo<Account>;

  @alias('accountId')
  declare rootAccount: AsyncBelongsTo<Account>;

  @or('lineItemId.accountId', 'accountId')
  declare account: unknown;

  @belongsTo('person', { async: true, inverse: null })
  declare personId: AsyncBelongsTo<Person>;

  @alias('personId')
  declare person: AsyncBelongsTo<Person>;

  @belongsTo('person', { async: true, inverse: null })
  declare authorId: AsyncBelongsTo<Person>;

  @alias('authorId')
  declare author: AsyncBelongsTo<Person>;

  @alias('author.fullName')
  declare authorName: unknown;

  @belongsTo('finance/line-item', { async: true, inverse: 'transactions' })
  declare lineItemId: AsyncBelongsTo<LineItem>;

  @alias('lineItemId')
  declare lineItem: AsyncBelongsTo<LineItem>;

  @alias('lineItem.id')
  declare line_item_id: string | null | undefined;

  @attr('number', { defaultValue: 0 })
  declare type: EnumValue<typeof TransactionType> | 0;

  @attr('number', { defaultValue: 0 })
  declare method: EnumValue<typeof PaymentMethod> | 0;

  @attr('string', { defaultValue: '' })
  declare personName: string;

  @attr('string', { defaultValue: '' })
  declare comment: string;

  @attr('string')
  declare date: string | undefined;

  @attr('string', { defaultValue: '' })
  declare reference: string;

  @attr('number', { defaultValue: 0 })
  declare amount: number;

  @computed('amount')
  get displayAmount() {
    return this.amount * -1;
  }

  @computed(
    'finance.allPaymentMethods.[]',
    'type',
    'method',
    'sourceTransaction.method',
    'refundMethodLabel',
  )
  get displayMethod() {
    const typeLabel = TransactionType.getLabel(this.type);
    switch (this.type) {
      case TransactionType.PAYMENT:
        return this.finance.getPaymentMethodLabel(this.method);
      case TransactionType.TRANSFER:
      case TransactionType.APPORTIONMENT:
        return (
          this.finance.getPaymentMethodLabel(
            this.get('sourceTransaction.method') || this.method,
          ) || typeLabel
        );
      case TransactionType.REFUND:
        if (isEmpty(this.refundMethodLabel)) {
          return typeLabel;
        }
        return `${typeLabel} (${this.refundMethodLabel})`;
      default:
        return typeLabel;
    }
  }

  @computed('type', 'method', 'features.sapFinance')
  get refundMethodLabel() {
    if (isEmpty(this.method) || !this.features.sapFinance) {
      return '';
    }
    switch (this.method) {
      case PaymentMethod.INTERNET:
        return 'EFT';
      default:
        return PaymentMethod.getLabel(this.method);
    }
  }

  @computed('amount', 'lineItem.taxPercentage')
  get taxAmount() {
    const rawRate = this.get('lineItem.taxPercentage') as number;
    if (rawRate > 0) {
      // Tax percentage is stored as an integer representing tenths of a percent
      const taxRate = rawRate / 100 / 100;
      const exclusive = this.displayAmount / (1 + taxRate);
      return this.displayAmount - exclusive;
    }
    return 0;
  }

  @computed(
    'childTransactions.@each.{amount,displayAmount,line_item_id}',
    'amount',
    'type',
  )
  get piaAmount() {
    switch (this.type) {
      case TransactionType.REFUND:
        // There isn't a separate PIA amount for refunds - the refund comes off the (PIA) account balance.
        return 0;
      case TransactionType.VOID:
      case TransactionType.WRITEOFF:
      case TransactionType.DISCOUNT___EXEMPTION:
      case TransactionType.ADJUSTMENT:
        // These transaction types only reduce the line item's balance;
        // they never touch Payment in Advance / account balances.
        return 0;
    }
    return (
      this.amount +
      resolved(this.childTransactions).reduce((val, transaction) => {
        // Transfers to siblings' PIA balances are positive, but line item payments are negative.
        // We want to subtract both from the transaction's total, so need to use different properties.
        let amount = transaction.displayAmount;
        if (isEmpty(transaction.line_item_id)) {
          amount = transaction.amount;
        }
        return val - amount;
      }, 0)
    );
  }

  // amountRefunded is the amount of this transaction that has been refunded.
  @attr('number', { defaultValue: 0 })
  declare amountRefunded: number;

  // isRefundable determines whether this transaction is refundable when using
  // the per-transaction refund approach (i.e. NSW SAP).
  @computed(
    'isCancelled',
    'type',
    'amount',
    'amountRefunded',
    'piaAmount',
    'lineItem.isRefundable',
    'childTransactions.[]',
  )
  get isRefundable(): boolean | undefined {
    if (this.isCancelled) {
      // Cancelled transactions can't be refunded.
      return false;
    }
    if (this.type === TransactionType.APPORTIONMENT) {
      if (isEmpty(this.line_item_id)) {
        // Apportioned to an account; this is refundable.
        return true;
      }
      // Apportioned to a Line Item - check if that's refundable.
      return !!this.get('lineItem.isRefundable');
    }
    if (this.type !== TransactionType.PAYMENT) {
      // We've handled apportionments above, so only payments are refundable.
      return false;
    }
    if (this.method === PaymentMethod.EXTERNAL) {
      // External / Third-Party payments are not refundable, in order to
      // prevent refunding them to the family.
      return false;
    }

    if (this.piaAmount > 0) {
      // Some amount of this Payment went to PIA, so that's refundable.
      return true;
    }

    if (!isEmpty(this.childTransactions)) {
      // Check for any refundable child transactions.
      return resolved(this.childTransactions).some((txn) => txn.isRefundable);
    }
  }

  // amountRefundable determines the amount that is currently refundable for
  // this Transaction (including any child transactions).
  @computed(
    'isRefundable',
    'type',
    'amount',
    'amountRefunded',
    'piaAmount',
    'line_item_id',
    'childTransactions.[]',
  )
  get amountRefundable() {
    if (!this.isRefundable) {
      // Non-refundable, so nothing can be refunded.
      return 0;
    }
    if (this.type === TransactionType.APPORTIONMENT) {
      // The (positive) amount of the apportionment less the amount already
      // refunded can be refunded.
      return Math.abs(this.amount) - this.amountRefunded;
    }

    // Start out with any PIA amount, which is always refundable.
    let amount = this.piaAmount;
    // Add any children that are refundable.
    if (!isEmpty(this.childTransactions)) {
      amount = resolved(this.childTransactions).reduce((total, txn) => {
        return total + Math.abs(txn.amount);
      }, amount);
    }
    // Remove any amount that's already refunded.
    return amount - this.amountRefunded;
  }

  // piaAmountRefundable determines the amount of PIA that is currently
  // refundable for this Transaction.
  @computed(
    'isRefundable',
    'type',
    'amount',
    'amountRefunded',
    'piaAmount',
    'line_item_id',
    'childTransactions.[]',
  )
  get piaAmountRefundable() {
    if (!this.isRefundable) {
      // Non-refundable, so nothing can be refunded.
      return 0;
    }
    if (this.type !== TransactionType.PAYMENT) {
      // Only the top-level payment counts here.
      return 0;
    }

    // Start out with any PIA amount, which is always refundable.
    const amount = this.piaAmount;

    if (isEmpty(this.childTransactions)) {
      // Just a PIA - remove any amount that's already refunded.
      return amount - this.amountRefunded;
    }

    // Remove any child transaction refunds from the overall refund amount.
    const refunded = resolved(this.childTransactions).reduce((total, txn) => {
      return total + txn.amountRefunded;
    }, 0);
    return amount - (this.amountRefunded - refunded);
  }

  // fullyRefunded determines whether this Transaction has been fully refunded.
  @computed('amountRefundable')
  get fullyRefunded() {
    return this.amountRefundable <= 0;
  }

  @or('person.fullName', 'personName')
  declare paidBy: unknown;

  @belongsTo('finance/transaction', {
    async: true,
    inverse: 'childTransactions',
  })
  declare sourceTransactionId: AsyncBelongsTo<Transaction>;

  @alias('sourceTransactionId')
  declare sourceTransaction: AsyncBelongsTo<Transaction>;

  @hasMany('finance/transaction', {
    async: true,
    inverse: 'sourceTransactionId',
  })
  declare childTransactions: AsyncHasMany<Transaction>;

  @alias('sourceTransaction.id')
  declare sourceTransaction_id: string | null | undefined;

  @attr({ defaultValue: 0 })
  declare stripeTransactionId: number; // TODO: change this to `belongsTo('finance/stripe-transaction')` when implemented

  @alias('stripeTransactionId')
  declare stripeTransaction: number;

  @attr('number', { defaultValue: 0 })
  declare nswCppTransactionId: number;

  // auditReason is required when updating or deleting an existing Transaction.
  // It is not stored in the Transaction record, but will become part of a TransactionAudit on the back end.
  @attr('string')
  declare auditReason: string | undefined;

  // xeroBatchNo has no default: serializers/finance/transaction.js:8 null marks
  // a transaction that is not in a batch.
  @attr('number')
  declare xeroBatchNo: number | undefined;

  @attr('boolean', { defaultValue: false })
  declare isPosted: boolean;

  // Cheque details - added for NSW, but available to others if needed.
  @attr('string', { defaultValue: '' })
  declare chequeNo: string;

  @attr('string', { defaultValue: '' })
  declare chequeBSB: string;

  @attr('string', { defaultValue: '' })
  declare chequeAccountNo: string;

  // Money Order details - as above.
  @attr('string', { defaultValue: '' })
  declare moneyOrderNumber: string;

  @attr('string', { defaultValue: '' })
  declare moneyOrderIssuer: string;

  @attr('string', { defaultValue: '' })
  declare moneyOrderPayee: string;

  // tillSessionId associates this Transaction with a Till Session.
  @belongsTo('finance/till-session', { async: true, inverse: null })
  declare tillSessionId: AsyncBelongsTo<TillSession>;

  @alias('tillSessionId')
  declare tillSession: AsyncBelongsTo<TillSession>;

  // Cancellation details.
  @attr('boolean', { defaultValue: false })
  declare isCancelled: boolean;

  @belongsTo('person', { async: true, inverse: null })
  declare cancelledById: AsyncBelongsTo<Person>;

  @alias('cancelledById')
  declare cancelledBy: AsyncBelongsTo<Person>;

  @attr('number')
  declare cancelledTime: number | undefined;

  @attr('string', { defaultValue: '' })
  declare cancellationReason: string;

  @attr('string', { defaultValue: '' })
  declare cancellationRequestedBy: string;

  @belongsTo('finance/till-session', { async: true, inverse: null })
  declare cancellationTillSessionId: AsyncBelongsTo<TillSession>;

  @alias('cancellationTillSessionId')
  declare cancellationTillSession: AsyncBelongsTo<TillSession>;

  @computed('type', 'author', 'cancelledBy')
  get cancelledOrRefundedBy() {
    if (this.type === TransactionType.REFUND) {
      return this.author;
    }
    return this.cancelledBy;
  }

  @computed('type', 'createTime', 'cancelledTime')
  get cancelledOrRefundedTime() {
    if (this.type === TransactionType.REFUND) {
      return this.createTime;
    }
    return this.cancelledTime;
  }

  @computed('type', 'comment', 'cancellationReason')
  get cancelledOrRefundedReason() {
    if (this.type === TransactionType.REFUND) {
      return this.comment;
    }
    return this.cancellationReason;
  }

  @belongsTo('finance/transaction', { async: true, inverse: null })
  declare refundedTransactionId: AsyncBelongsTo<Transaction>;

  @alias('refundedTransactionId')
  declare refundedTransaction: AsyncBelongsTo<Transaction>;

  // nswSapStatus is the SAP sync status for this Transaction, if applicable.
  @attr('number', { defaultValue: 0 })
  declare nswSapStatus: number;

  // nswSapMessage is the SAP sync message for this Transaction, if applicable.
  @attr('string', { defaultValue: '' })
  declare nswSapMessage: string;
}
